import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Plus, Pencil, Trash2, X, Check, Loader2 } from "lucide-react";
import {
  fetchStockParts,
  createStockPart,
  updateStockPart,
  deleteStockPart,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorMessage } from "@/lib/errors";
import { fmtMoney } from "@/lib/format";

/**
 * Maintain the stock parts catalog (commodity items kept on the bench
 * with default prices). When logging an actual part against a watch,
 * the user picks from this list and the form pre-fills name / type / cost.
 *
 * Default seed: acrylic crystal, tension-ring crystal, mainspring,
 * leather strap, stainless bracelet — user can edit, delete, or add their
 * own (crowns, gaskets, etc.). All values editable, no hardcoding.
 */
export default function StockParts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", part_type: "", default_cost: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchStockParts();
      setItems(r);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load stock parts"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim()) return;
    setBusy("__add__");
    try {
      await createStockPart({
        name: draft.name.trim(),
        part_type: draft.part_type.trim() || null,
        default_cost: draft.default_cost ? Number(draft.default_cost) : null,
      });
      toast.success(`Added "${draft.name.trim()}"`);
      setDraft({ name: "", part_type: "", default_cost: "" });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Add failed"));
    } finally { setBusy(null); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      name: item.name,
      part_type: item.part_type || "",
      default_cost: item.default_cost ?? "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft(null); };

  const saveEdit = async () => {
    if (!editDraft.name.trim()) return;
    setBusy(editingId);
    try {
      await updateStockPart(editingId, {
        name: editDraft.name.trim(),
        part_type: editDraft.part_type.trim() || null,
        default_cost: editDraft.default_cost === "" || editDraft.default_cost === null
          ? null
          : Number(editDraft.default_cost),
      });
      toast.success("Updated");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Update failed"));
    } finally { setBusy(null); }
  };

  const onDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const item = confirmDelete;
    setConfirmDelete(null);
    setBusy(item.id);
    try {
      await deleteStockPart(item.id);
      toast.success(`Deleted "${item.name}"`);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-[#0A0604]" data-testid="stock-parts-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/data" className="text-sm text-[#3A2A10] hover:text-[#0A0604] flex items-center gap-1" data-testid="back-to-data">
            <ChevronLeft size={16} /> Data
          </Link>
          <h1 className="font-serif-display text-3xl">Stock Parts</h1>
        </div>
        {!adding && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdding(true)}
            className="h-9 border-[#8B6A1F]/45 bg-transparent text-[#3A2A10] hover:bg-[#2B2214]/[0.08] gap-1.5"
            data-testid="sp-add"
          >
            <Plus size={14} /> Add stock part
          </Button>
        )}
      </div>

      <p className="text-sm text-[#3A2A10] mb-6 max-w-3xl">
        Commodity items you keep on the bench with a default cost.
        When you log a part on a service, pick from this list and the form
        pre-fills name, type, and cost. Edit prices any time — historical
        parts already logged keep whatever cost was recorded at the time.
      </p>

      {adding && (
        <form
          onSubmit={onAdd}
          className="border border-[#8B6A1F]/35 rounded-lg bg-[#F5E9C5]/40 p-4 mb-4 grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end"
        >
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Name</label>
            <Input
              autoFocus
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Crown — chrome"
              className="dark-input h-9 mt-1"
              data-testid="sp-add-name"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Type</label>
            <Input
              value={draft.part_type}
              onChange={(e) => setDraft({ ...draft, part_type: e.target.value })}
              placeholder="optional"
              className="dark-input h-9 mt-1"
              data-testid="sp-add-type"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Default cost</label>
            <Input
              type="number"
              step="0.01"
              value={draft.default_cost}
              onChange={(e) => setDraft({ ...draft, default_cost: e.target.value })}
              placeholder="0.00"
              className="dark-input h-9 mt-1"
              data-testid="sp-add-cost"
            />
          </div>
          <div className="flex gap-1.5">
            <Button type="submit" size="sm" className="h-9 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={!draft.name.trim() || busy === "__add__"} data-testid="sp-add-confirm">
              <Check size={14} className="mr-1" /> Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft({ name: "", part_type: "", default_cost: "" }); }} className="h-9 text-[#3A2A10]">
              <X size={14} />
            </Button>
          </div>
        </form>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#3A2A10]">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#3A2A10]">No stock parts yet — click "Add stock part" to start.</div>
      ) : (
        <div className="border border-[#8B6A1F]/35 rounded-lg bg-[#FBF3D9]/60 overflow-hidden">
          <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_auto] gap-3 px-5 py-2.5 bg-[#FBF3D9] border-b border-[#8B6A1F]/35 text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">
            <div>Name</div>
            <div>Type</div>
            <div>Default cost</div>
            <div></div>
          </div>
          <ul className="divide-y divide-[#8B6A1F]/15">
            {items.map((it) => {
              const isEditing = editingId === it.id;
              return (
                <li
                  key={it.id}
                  className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-3 px-5 py-2.5 hover:bg-[#F5E9C5]/40 items-center"
                  data-testid={`sp-row-${it.id}`}
                >
                  {isEditing ? (
                    <>
                      <Input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="dark-input h-8" autoFocus />
                      <Input value={editDraft.part_type} onChange={(e) => setEditDraft({ ...editDraft, part_type: e.target.value })} className="dark-input h-8" />
                      <Input type="number" step="0.01" value={editDraft.default_cost} onChange={(e) => setEditDraft({ ...editDraft, default_cost: e.target.value })} className="dark-input h-8" />
                      <div className="flex gap-1.5">
                        <Button size="sm" onClick={saveEdit} className="h-8 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={busy === editingId}>
                          <Check size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8 text-[#3A2A10]">
                          <X size={14} />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-mono-data text-sm text-[#0A0604]">{it.name}</div>
                      <div className="text-sm text-[#3A2A10]">{it.part_type || "—"}</div>
                      <div className="font-mono-data text-sm text-[#0A0604]">{it.default_cost != null ? fmtMoney(it.default_cost) : "—"}</div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(it)} disabled={busy === it.id} className="text-[#3A2A10] hover:text-[#0A0604]" title="Edit" data-testid={`sp-edit-${it.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(it)} disabled={busy === it.id} className="text-rose-700 hover:text-rose-900 hover:bg-rose-50" title="Delete" data-testid={`sp-delete-${it.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#FBF3D9] border-[#8B6A1F]/45">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#0A0604]">
              Delete "{confirmDelete?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              Existing parts already logged on services keep whatever cost was
              recorded — only the catalog entry is removed. You can re-add it
              later if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#8B6A1F]/45 bg-transparent text-[#3A2A10] hover:bg-[#2B2214]/[0.06]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={onDeleteConfirmed} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="sp-confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
